"use client";
import { useState, useEffect } from "react";
import axios from "axios";
import Cookies from "js-cookie";
import { toast, Toaster } from "sonner";
import React from "react";
import { Progress } from "@/components/ui/progress";
import {Turnstile} from "@marsidev/react-turnstile";
import { useTranslations } from "next-intl";

const BusinessNameGeneratorForm = () => {
    const t = useTranslations("BusinessNameGeneratorForm");
    const [keyword, setKeyword] = useState("");
    const [industry, setIndustry] = useState("");
    const [style, setStyle] = useState("");
    const [productDescription, setProductDescription] = useState("");
    const [language, setLanguage] = useState("");
    const [generatedNames, setGeneratedNames] = useState<
        { name: string; domain: string; domainAvailable: boolean }[]
    >([]);
    const [loading, setLoading] = useState(false);
    const [generateCount, setGenerateCount] = useState(0);
    const maxGenerateCount = 5;
    const [progress, setProgress] = useState(0);

    const [turnstileToken, setTurnstileToken] = useState<string | null>(null);

    useEffect(() => {
        const savedGenerateCount = Cookies.get("generateCount");
        if (savedGenerateCount) {
            setGenerateCount(parseInt(savedGenerateCount));
        }
    }, []);

    const handleGenerateNames = async () => {
        if (!keyword || !industry || !style || !productDescription || !language) {
            toast.error(t("error.fillRequiredFields"));
            return;
        }

        if (!turnstileToken) {
            toast.error(t("error.turnstileToken"));
            return;
        }

        if (generateCount >= maxGenerateCount) {
            toast.error(t("error.maxRegenerationLimit"));
            return;
        }

        setLoading(true);
        setProgress(0);

        const interval = setInterval(() => {
            setProgress((oldProgress) => (oldProgress < 90 ? oldProgress + 10 : oldProgress));
        }, 500);

        try {
            const response = await axios.post("/api/generate-business-names", {
                keyword,
                industry,
                style,
                productDescription,
                language,
                turnstileToken,
            });

            if (response.data.error) {
                toast.error(response.data.error);
            } else {
                setGeneratedNames(response.data.names);
                const newGenerateCount = generateCount + 1;
                setGenerateCount(newGenerateCount);
                Cookies.set("generateCount", newGenerateCount.toString(), {
                    expires: 1,
                });
            }
        } catch (error) {
            console.error("Error while generating business name:", error);
            toast.error(t("error.failedGenerateBusinessName"));
        } finally {
            clearInterval(interval);
            setProgress(100);
            setTimeout(() => setLoading(false), 500);
            setLoading(false);
        }
    };

    const handleReset = () => {
        setKeyword("");
        setIndustry("");
        setStyle("");
        setProductDescription("");
        setLanguage("");
        setGeneratedNames([]);
        setGenerateCount(0);
        Cookies.remove("generateCount");
    };

    return (
        <>
            <Toaster />
            <div className="container mx-auto px-4 py-8">
                <h1 className="mb-10 mt-5 text-center text-3xl font-bold">{t("title")}</h1>
                <div className="flex flex-col items-start gap-8 lg:flex-row lg:items-start">
                    <div className="rounded-lg bg-transparent p-6 shadow-md lg:w-1/2">
                        {generatedNames.length > 0 ? (
                            <>
                                <h2 className="mb-6 text-center text-2xl font-semibold text-gray-800">
                                    {t("generatedtitle")}
                                </h2>
                                <ul className="space-y-4">
                                    {generatedNames.map((item, index) => (
                                        <li
                                            key={index}
                                            className={`rounded-md p-4 shadow-md ${item.domainAvailable ? "text-green-600" : "text-red"
                                                }`}
                                        >
                                            {index + 1}. {item.name} --- ({item.domain}) {item.domainAvailable ? "" : t("domainNotAvailable")}
                                        </li>
                                    ))}
                                </ul>

                                <div className="mt-6 flex justify-between">
                                    {loading ? (
                                        <div className="flex flex-col items-center w-full">
                                            <Progress value={progress} className="w-full mb-2" />
                                            <p className="text-primary font-semibold">{t("generatingNames")}</p>
                                        </div>
                                    ) : (
                                        <button
                                            onClick={handleGenerateNames}
                                            className="bg-blue-600 text-white hover:bg-blue-700 rounded-lg px-6 py-3 font-medium transition-all shadow-lg mb-5"
                                        >
                                            {t("regenerateNames")}
                                        </button>
                                    )}
                                    <button
                                        onClick={handleReset}
                                        className="bg-gray-100 border border-gray-300 hover:bg-gray-200 rounded-lg px-6 py-3 font-medium transition-all mb-5 ml-4"
                                    >
                                        {t("recreate")}
                                    </button>
                                </div>

                                <button
                                    onClick={() => window.open('https://s.id/CV3QT', '_blank', 'noopener,noreferrer')}
                                    className="bg-purple-500 text-white hover:bg-purple-600 w-full rounded-lg px-6 py-3 font-medium transition-all shadow-lg mb-5">
                                    {t("buydomain")}
                                    <span className="ml-2 text-sm opacity-90">{t("promo")}</span>
                                </button>
                            </>
                        ) : (
                            <>
                                <h2 className="mb-6 text-center text-2xl font-bold">
                                    {t("titleForm")}
                                </h2>
                                <div className="mb-4">
                                    <label className="mb-1 block text-base font-medium text-gray-700 dark:text-gray-400">
                                        {t("keyword")}
                                    </label>
                                    <input
                                        type="text"
                                        value={keyword}
                                        onChange={(e) => setKeyword(e.target.value)}
                                        className="w-full rounded-md border border-gray-300 p-2 text-base focus:outline-none focus:ring-2 focus:ring-primary"
                                        required
                                    />
                                </div>
                                <div className="mb-4">
                                    <label className="mb-1 block text-base font-medium text-gray-700 dark:text-gray-400">
                                        {t("industry")}
                                    </label>
                                    <select
                                        value={industry}
                                        onChange={(e) => setIndustry(e.target.value)}
                                        className="w-full rounded-md border border-gray-300 p-2 text-base focus:outline-none focus:ring-2 focus:ring-primary"
                                        required
                                    >
                                        <option value="">{t("selectIndustry")}</option>
                                        <option value="Technology">{t("technology")}</option>
                                        <option value="Health">{t("health")}</option>
                                        <option value="Finance">{t("finance")}</option>
                                        <option value="Education">{t("education")}</option>
                                        <option value="Retail">{t("retail")}</option>
                                        <option value="Food and Beverage">{t("foodandbeverage")}</option>
                                        <option value="Hospitality">{t("hospitality")}</option>
                                    </select>
                                </div>
                                <div className="mb-4">
                                    <label className="mb-1 block text-base font-medium text-gray-700 dark:text-gray-400">
                                        {t("style")}
                                    </label>
                                    <select
                                        value={style}
                                        onChange={(e) => setStyle(e.target.value)}
                                        className="w-full rounded-md border border-gray-300 p-2 text-base focus:outline-none focus:ring-2 focus:ring-primary"
                                        required
                                    >
                                        <option value="">{t("selecetStyle")}</option>
                                        <option value="modern">{t("modern")}</option>
                                        <option value="traditional">{t("traditional")}</option>
                                        <option value="short">{t("short")}</option>
                                        <option value="unique">{t("unique")}</option>
                                        <option value="elegant">{t("elegant")}</option>
                                        <option value="casual">{t("casual")}</option>
                                        <option value="professional">{t("professional")}</option>
                                    </select>
                                </div>
                                <div className="mb-4">
                                    <label className="mb-1 block text-base font-medium text-gray-700 dark:text-gray-400">
                                        {t("whatDoYouSell")}
                                    </label>
                                    <input
                                        type="text"
                                        value={productDescription}
                                        onChange={(e) => setProductDescription(e.target.value)}
                                        className="w-full rounded-md border border-gray-300 p-2 text-base focus:outline-none focus:ring-2 focus:ring-primary"
                                        required
                                    />
                                    <p className="text-sm text-gray-500 dark:text-gray-400">
                                        {t("example")}
                                    </p>
                                </div>
                                <div className="mb-4">
                                    <label className="mb-1 block text-base font-medium text-gray-700 dark:text-gray-400">
                                        {t("language")}
                                    </label>
                                    <input
                                        type="text"
                                        value={language}
                                        onChange={(e) => setLanguage(e.target.value)}
                                        className="w-full rounded-md border border-gray-300 p-2 text-base focus:outline-none focus:ring-2 focus:ring-primary"
                                        required
                                    />
                                    <p className="text-sm text-gray-500 dark:text-gray-400">
                                        {t("enterLanguage")}
                                    </p>
                                </div>
                                {/* Turnstile CAPTCHA */}
                        <div className="mb-4">
                            <Turnstile
                                siteKey="0x4AAAAAAA29-0PYk7u7JLd5"
                                onSuccess={(token) => setTurnstileToken(token)}
                                onExpire={() => setTurnstileToken(null)}
                                options={{ theme: "light" }}
                            />
                        </div>
                                <div className="mt-6 text-center">
                                    {loading ? (
                                        <div className="flex flex-col items-center">
                                            <Progress value={progress} className="w-full mb-2" />
                                            <p className="text-primary font-semibold">{t("generatingBusinessNames")}</p>
                                        </div>
                                    ) : (
                                        <button
                                            onClick={handleGenerateNames}
                                            className="bg-primary text-white hover:bg-primary shadow-md shadow-primary/40 hover:shadow-[0_2px_8px] hover:shadow-primary/40 inline-block w-full rounded-md text-base font-semibold h-10 mb-5"
                                            disabled={loading}
                                        >
                                            {t("generateNames")}
                                        </button>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                    <div className="mt-8 lg:mt-0 lg:w-1/2">
                        <h2 className="mb-4 text-2xl font-bold">{t("content.title")}</h2>
                        <p className="mb-6 text-lg">
                            {t("content.p1")}
                        </p>
                        <p className="mb-6 text-lg">
                            {t("content.p2")}
                        </p>
                        <p className="mb-6 text-lg">
                            {t("content.p3")}
                        </p>
                        <p className="mb-6 text-lg">
                            {t("content.p4")}
                        </p>
                    </div>
                </div>
            </div>
        </>
    );
};

export default React.memo(BusinessNameGeneratorForm);
